import React, { useRef } from "react";
import { Flame, Shuffle, ArrowRightLeft, Shield } from "lucide-react";
import { useInView } from "../hooks/useInView";
import { whyChoosePillars } from "../data/mock";

const iconMap = {
  Flame,
  Shuffle,
  ArrowRightLeft,
  Shield,
};

export const WhyChoose = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { threshold: 0.1 });

  return (
    <section
      id="why-choose"
      ref={sectionRef}
      className="section-padding relative overflow-hidden"
      style={{ backgroundColor: "#0F0F0F" }}
    >
      {/* Subtle decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div
          className={`text-center max-w-2xl mx-auto mb-20 ${
            isInView ? "animate-fade-in-up" : "opacity-0"
          }`}
        >
          <span className="text-gold text-sm font-semibold tracking-widest uppercase mb-4 block">
            Why Choose Us
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            The Lobben <span className="text-gold">Difference</span>
          </h2>
          <p className="text-white/50 text-lg leading-relaxed">
            We don't just play music. We engineer unforgettable moments with
            trust, adaptability, and flawless execution.
          </p>
        </div>

        {/* Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {whyChoosePillars.map((pillar, index) => {
            const Icon = iconMap[pillar.icon];
            return (
              <div
                key={index}
                className={`group relative p-8 lg:p-10 rounded-2xl border border-white/10 hover:border-gold/30 ${
                  isInView ? "animate-fade-in-up" : "opacity-0"
                }`}
                style={{
                  animationDelay: `${(index + 1) * 200}ms`,
                  animationFillMode: "both",
                  backgroundColor: "rgba(255,255,255,0.03)",
                  backdropFilter: "blur(4px)",
                  transition: "border-color 0.3s ease, background-color 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.06)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.03)";
                }}
              >
                {/* Icon */}
                <div
                  className="w-14 h-14 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center mb-6 group-hover:bg-gold/20"
                  style={{ transition: "background-color 0.3s ease" }}
                >
                  {Icon && <Icon className="h-7 w-7 text-gold" />}
                </div>

                {/* Content */}
                <h3 className="font-display text-2xl font-semibold text-white mb-3">
                  {pillar.title}
                </h3>
                <p className="text-white/50 leading-relaxed mb-4">
                  {pillar.description}
                </p>

                {/* Highlight */}
                <span className="inline-flex items-center text-gold text-sm font-medium">
                  {pillar.highlight}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
